import styled, { css } from 'styled-components'
import { stopPropagation } from '../../../utils/dom-utils'
import { ChevronLeftIcon, ChevronRightIcon } from '../icons'
import type { NavVariant, NavPosition } from './types'

interface NavButtonsProps {
    canScrollPrev: boolean
    canScrollNext: boolean
    onPrev: () => void
    onNext: () => void
    variant?: NavVariant
    position?: NavPosition
    buttonBackground?: string
    buttonDisabledOpacity?: number
}

type OverlayButtonProps = {
    $variant: NavVariant
    $background?: string
    $disabledOpacity?: number
}

type TopRightButtonProps = {
    $background?: string
    $disabledOpacity?: number
}

const DARK_OVERLAY_BG = 'rgba(33, 33, 33, 0.6)'
const WHITE_CIRCLE_BG = 'rgba(255, 255, 255, 0.9)'
const WHITE_BG = '#ffffff'

const overlayButtonBase = css`
    position: absolute;
    top: 45%;
    transform: translateY(-50%);
    box-shadow:
        0px 5px 5px 0px #00000014,
        0px 3px 14px 0px #0000000a,
        0px 8px 10px 0px #0000000f;
    border-radius: 100%;
    margin: 0 1.25rem;
    height: 2rem;
    width: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 1;
    z-index: 10;
    border: none;
    cursor: pointer;
    transition: opacity 200ms ease-in;

    svg {
        min-width: 1.5rem;
        min-height: 1.5rem;
    }
`

const PrevButton = styled.button<OverlayButtonProps>`
    ${overlayButtonBase}
    left: 0;
    background-color: ${({ $variant, $background }) =>
        $background ?? ($variant === 'white-circle' ? WHITE_CIRCLE_BG : DARK_OVERLAY_BG)};

    svg path {
        stroke: ${({ $variant }) => ($variant === 'white-circle' ? '#333' : 'white')};
    }

    &:hover {
        opacity: ${({ $variant }) => ($variant === 'white-circle' ? 0.8 : 1)} !important;
    }

    &:disabled {
        opacity: ${({ $disabledOpacity }) => $disabledOpacity ?? 0} !important;
        pointer-events: none;
    }
`

const NextButton = styled.button<OverlayButtonProps>`
    ${overlayButtonBase}
    right: 0;
    background-color: ${({ $variant, $background }) =>
        $background ?? ($variant === 'white-circle' ? WHITE_CIRCLE_BG : DARK_OVERLAY_BG)};

    svg path {
        stroke: ${({ $variant }) => ($variant === 'white-circle' ? '#333' : 'white')};
    }

    &:hover {
        opacity: ${({ $variant }) => ($variant === 'white-circle' ? 0.8 : 1)} !important;
    }

    &:disabled {
        opacity: ${({ $disabledOpacity }) => $disabledOpacity ?? 0} !important;
        pointer-events: none;
    }
`

const TopRightContainer = styled.div<{ $position?: NavPosition }>`
    display: none;

    @media (min-width: 1024px) {
        display: flex;
        position: absolute;
        gap: 8px;
        top: ${({ $position }) => $position?.desktopTop ?? '0'};
        right: ${({ $position }) => $position?.desktopRight ?? '0'};
        z-index: 2;
    }

    @media (min-width: 1440px) {
        top: ${({ $position }) => $position?.wideTop ?? $position?.desktopTop ?? '0'};
        right: ${({ $position }) => $position?.wideRight ?? $position?.desktopRight ?? '0'};
    }
`

const TopRightButton = styled.button<TopRightButtonProps>`
    background-color: ${({ $background }) => $background ?? WHITE_BG};
    box-shadow:
        0 0.6875rem 0.9375rem 0 #00000014,
        0 0.5625rem 2.875rem 0 #0000000a,
        0 1.5rem 2.375rem 0 #0000000f;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem;
    transition: opacity 200ms ease-in;

    svg {
        height: 20px;
        width: 20px;
    }

    &:hover:not(:disabled) {
        opacity: 0.8;
    }

    &:disabled {
        opacity: ${({ $disabledOpacity }) => $disabledOpacity ?? 0.5};
        cursor: default;
        pointer-events: none;
    }
`

export function EmblaCarouselNav({
    canScrollPrev,
    canScrollNext,
    onPrev,
    onNext,
    variant = 'dark-overlay',
    position,
    buttonBackground,
    buttonDisabledOpacity,
}: NavButtonsProps) {
    if (variant === 'top-right') {
        return (
            <TopRightContainer $position={position} onClick={stopPropagation}>
                <TopRightButton
                    onClick={onPrev}
                    disabled={!canScrollPrev}
                    aria-label="Previous slide"
                    $background={buttonBackground}
                    $disabledOpacity={buttonDisabledOpacity}
                >
                    <ChevronLeftIcon />
                </TopRightButton>
                <TopRightButton
                    onClick={onNext}
                    disabled={!canScrollNext}
                    aria-label="Next slide"
                    $background={buttonBackground}
                    $disabledOpacity={buttonDisabledOpacity}
                >
                    <ChevronRightIcon />
                </TopRightButton>
            </TopRightContainer>
        )
    }

    return (
        <div onClick={stopPropagation}>
            <PrevButton
                $variant={variant}
                $background={buttonBackground}
                $disabledOpacity={buttonDisabledOpacity}
                onClick={onPrev}
                disabled={!canScrollPrev}
                aria-label="Previous slide"
            >
                <ChevronLeftIcon />
            </PrevButton>
            <NextButton
                $variant={variant}
                $background={buttonBackground}
                $disabledOpacity={buttonDisabledOpacity}
                onClick={onNext}
                disabled={!canScrollNext}
                aria-label="Next slide"
            >
                <ChevronRightIcon />
            </NextButton>
        </div>
    )
}
